import { useEffect, useRef } from "react";
import { useTheme } from "styled-components";
import { useWorld } from "../hooks/use-world";
import { useMapInstance } from "components/Map";
import { gameToCanvas, gamePixelToLatLng } from "../lib/geo-projection";
import { drawTileCellRect } from "../lib/tile-renderer";
import { spriteImagePath, DEFAULT_MONSTER_SPRITE, GRID_SIZE } from "../config/constants";

// Fallback colours by entity type (meta?.color from map data takes precedence)
const ENTITY_COLORS = {
  player: "#88aaff",
  monster: "#cc2222",
  npc: "#4488cc",
  item: "#FFD700",
  treasure: "#FFD700", // backward compat
  ladder: "#c8a84b",
  exit: "#44cc66",
};

// Replicates CSS `background-size: cover; background-position: bottom center`
const drawImageCoverBottom = (ctx, img, dx, dy, dw, dh) => {
  const iw = img.naturalWidth;
  const ih = img.naturalHeight;
  const scale = Math.max(dw / iw, dh / ih);
  const sw = dw / scale;
  const sh = dh / scale;
  ctx.drawImage(img, (iw - sw) / 2, ih - sh, sw, sh, dx, dy, dw, dh);
};

const loadImage = (cache, src) => {
  if (cache[src] instanceof Image) return cache[src];
  if (cache[src]) return null; // 'loading' or 'error'
  cache[src] = "loading";
  const img = new Image();
  img.src = src;
  img.onload = () => {
    cache[src] = img;
  };
  img.onerror = () => {
    cache[src] = "error";
  };
  return null;
};
const loadSprite = (cache, name) => loadImage(cache, spriteImagePath(name));

// Canvas renders the dungeon tile grid and all game entities on a canvas
// that sits on top of the Leaflet map.  On every animation frame the canvas is
// cleared and redrawn using Leaflet's latLngToContainerPoint, so tiles and sprites
// scale and translate automatically as the map zooms or pans.
export const Canvas = ({
  tiles,
  decorationTiles,
  overlayTiles,
  visibility,
  entities,
  gridSize,
  anchor,
}) => {
  const mapInstance = useMapInstance();
  const canvasRef = useRef(null);
  const { spritesRef, frame, remotePlayers, currentLevel, attackFlashRef, inventory, droppedItems, heldItemId } = useWorld();
  const theme = useTheme();
  const remotePlayersRef = useRef(remotePlayers);
  const currentLevelRef = useRef(currentLevel);
  useEffect(() => {
    remotePlayersRef.current = remotePlayers;
  }, [remotePlayers]);
  useEffect(() => {
    currentLevelRef.current = currentLevel;
  }, [currentLevel]);

  const heldItemRef = useRef(null);
  useEffect(() => {
    heldItemRef.current = heldItemId ? (inventory.find((i) => i.id === heldItemId) ?? null) : null;
  }, [heldItemId, inventory]);

  const imageCacheRef = useRef({});
  // fixedHalfTile is the half-tile canvas size at the current zoom — recomputed only on zoom change
  const fixedHalfTileRef = useRef(null);
  const lastZoomRef = useRef(null);

  // id → { color, entityType } from mapData entities
  const entityMetaRef = useRef({});
  useEffect(() => {
    const meta = {};
    entities?.forEach((e) => {
      if (e.id)
        meta[e.id] = { color: e.backgroundColor, entityType: e.entityType };
    });
    entityMetaRef.current = meta;
  }, [entities]);

  // Create canvas imperatively inside the Leaflet container div
  useEffect(() => {
    if (!mapInstance) return;
    const container = mapInstance.getContainer();

    const canvas = document.createElement("canvas");
    canvas.style.cssText =
      "position:absolute;top:0;left:0;pointer-events:none;z-index:1000";
    container.appendChild(canvas);
    canvasRef.current = canvas;

    return () => {
      canvas.remove();
      canvasRef.current = null;
    };
  }, [mapInstance]);

  // Redraw on every animation frame
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !mapInstance) return;

    const container = mapInstance.getContainer();
    const W = container.offsetWidth;
    const H = container.offsetHeight;
    if (!W || !H) return;

    // Center the map view on the player before drawing so the projection is
    // always correct.  The follow effect in Game fires after this effect
    // (parent effects run after children), so we own the view here.
    const playerSprite = spritesRef.current["player"];
    if (playerSprite?.position) {
      const { x, y, width = GRID_SIZE, height = GRID_SIZE } = playerSprite.position;
      const { lat, lng } = gamePixelToLatLng(
        anchor.lat,
        anchor.lng,
        x + width / 2,
        y + height / 2
      );
      mapInstance.setView([lat, lng], mapInstance.getZoom(), {
        animate: false,
      });
    }

    canvas.width = W;
    canvas.height = H;

    const ctx = canvas.getContext("2d");
    // Crisp pixel-art sampling — smoothing bleeds neighboring sheet pixels into a tile's
    // edges when drawImage scales a source rect to a different destination size.
    ctx.imageSmoothingEnabled = false;
    const themeBg = theme?.colors?.("background");

    // Fill entire canvas with theme background before drawing anything
    ctx.fillStyle = themeBg;
    ctx.fillRect(0, 0, W, H);

    // project game coords → canvas pixel via Leaflet
    const project = (gx, gy) =>
      gameToCanvas(anchor.lat, anchor.lng, gx, gy, mapInstance);

    // ── Tile layer ────────────────────────────────────────────────────────────
    tiles.forEach((row, r) => {
      row.forEach((tile, c) => {
        if (!tile?.bgColor && !tile?.texture && !tile?.tileset) return;

        const tl = project(c * gridSize, r * gridSize);
        const br = project((c + 1) * gridSize, (r + 1) * gridSize);

        const w = br.x - tl.x;
        const h = br.y - tl.y;
        if (w < 0.5 || h < 0.5) return;

        // Walkable colour-fallback tiles are translucent so any background shows through
        const isWalkableColor = tile.bgColor && !tile.tileset && !tile.texture && !tile.solid;
        ctx.globalAlpha = isWalkableColor ? 0.8 : 1;
        drawTileCellRect(ctx, tile, tl.x, tl.y, w, h, imageCacheRef.current);
        ctx.globalAlpha = 1;
      });
    });

    // ── Decoration tile layer (above base tiles, below entities) ─────────────
    if (decorationTiles) {
      decorationTiles.forEach((row, r) => {
        row.forEach((tile, c) => {
          if (!tile?.texture && !tile?.bgColor && !tile?.tileset) return;

          const tl = project(c * gridSize, r * gridSize);
          const br = project((c + 1) * gridSize, (r + 1) * gridSize);
          const w = br.x - tl.x;
          const h = br.y - tl.y;
          if (w < 0.5 || h < 0.5) return;

          ctx.globalAlpha = tile.opacity ?? 1;
          drawTileCellRect(ctx, tile, tl.x, tl.y, w, h, imageCacheRef.current);
          ctx.globalAlpha = 1;
        });
      });
    }

    // ── Attack flash ─────────────────────────────────────────────────────────
    const flash = attackFlashRef?.current;
    if (flash && flash.framesLeft > 0) {
      const fp = project(flash.gx, flash.gy);
      if (fp) {
        const alpha = (flash.framesLeft / 10) * 0.55;
        const color = flash.hit
          ? theme?.colors("link", alpha)
          : theme.colors("foreground", alpha);
        ctx.beginPath();
        ctx.arc(fp.x, fp.y, gridSize * 0.6, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
      }
      flash.framesLeft--;
      if (flash.framesLeft <= 0) attackFlashRef.current = null;
    }

    // ── Entity / sprite layer ─────────────────────────────────────────────────
    // Recompute tile→pixel scale only when zoom changes; projection math is expensive.
    const zoom = mapInstance.getZoom();
    if (zoom !== lastZoomRef.current) {
      const refA = project(0, 0);
      const refB = project(gridSize, 0);
      fixedHalfTileRef.current =
        refA && refB ? Math.abs(refB.x - refA.x) / 2 : gridSize / 2;
      lastZoomRef.current = zoom;
    }
    const fixedHalfTile = fixedHalfTileRef.current ?? gridSize / 2;

    const entityMeta = entityMetaRef.current;

    // Painter's algorithm: draw entities sorted by their bottom (feet) y so
    // entities lower on the map are drawn later and appear above entities
    // higher up, regardless of whether they're local sprites or remote players.
    const drawLocalSprite = (pos) => {
      const entityType = pos.config?.entityType;
      const isPlayer = pos.id === "player";

      const hw = (pos.width || gridSize) / 2;
      const hh = (pos.height || gridSize) / 2;
      const p = project(pos.x + hw, pos.y + hh);

      // Radius scales with the projected tile size
      const pEdge = project(pos.x + hw * 2, pos.y + hh);
      const halfTile = pEdge ? Math.abs(pEdge.x - p.x) : fixedHalfTile;
      const radius = Math.max(2, halfTile * 0.85);

      const meta = isPlayer ? null : entityMeta[pos.id];
      const color =
        (isPlayer ? ENTITY_COLORS.player : null) ??
        meta?.color ??
        ENTITY_COLORS[entityType] ??
        "#ffffff";

      if (entityType === "exit") {
        // Draw exit as a green square with a door/exit arrow symbol
        const size = radius * 1.8;
        ctx.fillStyle = "#0d2e14";
        ctx.fillRect(p.x - size / 2, p.y - size / 2, size, size);
        ctx.strokeStyle = "#44cc66";
        ctx.lineWidth = 1.5;
        ctx.strokeRect(p.x - size / 2, p.y - size / 2, size, size);
        ctx.fillStyle = "#44cc66";
        ctx.font = `bold ${Math.max(7, size * 0.65)}px Silkscreen`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("⬆", p.x, p.y);
        return;
      }

      if (entityType === "ladder") {
        // Draw ladder as a filled square with an up or down arrow
        const size = radius * 1.6;
        ctx.fillStyle = color;
        ctx.fillRect(p.x - size / 2, p.y - size / 2, size, size);
        ctx.strokeStyle = "rgba(0,0,0,0.6)";
        ctx.lineWidth = 1;
        ctx.strokeRect(p.x - size / 2, p.y - size / 2, size, size);

        // Arrow symbol
        const dir = pos.config?.direction ?? "down";
        ctx.fillStyle = "#1a1208";
        ctx.font = `bold ${Math.max(7, size * 0.7)}px Silkscreen`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(dir === "up" ? "▲" : "▼", p.x, p.y);
        return;
      }

      const entitySprite =
        isPlayer || entityType === "npc"
          ? pos.config?.sprite
          : entityType === "monster"
            ? (pos.config?.sprite ?? DEFAULT_MONSTER_SPRITE)
            : null;
      if (entitySprite) {
        const img = loadSprite(imageCacheRef.current, entitySprite);
        const spriteW = fixedHalfTile * 2;
        const spriteH = spriteW * 2;
        const sx = p.x - fixedHalfTile;
        const sy = p.y + fixedHalfTile - spriteH;
        if (img instanceof Image) {
          drawImageCoverBottom(ctx, img, sx, sy, spriteW, spriteH);
        } else {
          // Placeholder while loading
          ctx.beginPath();
          ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
          ctx.fillStyle = color;
          ctx.fill();
        }
      } else {
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.strokeStyle = "rgba(255,255,255,0.55)";
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      // Health bar + name label for monsters
      if (entityType === "monster") {
        const spriteData = spritesRef.current[pos.id];
        const hp = spriteData?.health;
        const monsterName = pos.config?.name;

        let labelY = p.y - radius - 2;

        if (monsterName) {
          const fontSize = Math.max(6, radius * 0.8);
          ctx.font = `bold ${fontSize}px Silkscreen`;
          ctx.textAlign = "center";
          ctx.textBaseline = "bottom";
          ctx.fillStyle = "rgba(0,0,0,0.6)";
          ctx.fillText(monsterName, p.x + 1, labelY);
          ctx.fillStyle = "#ffaaaa";
          ctx.fillText(monsterName, p.x, labelY - 1);
          labelY -= fontSize + 2;
        }

        if (hp && hp.current < hp.max) {
          const barW = radius * 2.2;
          const barH = Math.max(1, radius * 0.35);
          const barX = p.x - barW / 2;
          const barY = labelY - barH;
          const pct = hp.current / hp.max;
          ctx.fillStyle = "rgba(0,0,0,0.6)";
          ctx.fillRect(barX, barY, barW, barH);
          ctx.fillStyle =
            pct > 0.5 ? "#44cc44" : pct > 0.25 ? "#cccc44" : "#cc4444";
          ctx.fillRect(barX, barY, barW * pct, barH);
        }
      }

      // Username label for the local player
      const username = isPlayer ? pos.config?.username : null;
      if (username) {
        const fontSize = Math.max(7, radius * 0.9);
        ctx.font = `bold ${fontSize}px Silkscreen`;
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillStyle = "rgba(0,0,0,0.6)";
        ctx.fillText(username, p.x + 1, p.y - radius);
        ctx.fillStyle = "#ccddff";
        ctx.fillText(username, p.x, p.y - radius - 1);
      }
    };

    const drawRemotePlayer = (player) => {
      const hw = gridSize / 2;
      const p = project(player.x + hw, player.y + hw);
      if (!p) return;

      const pEdge = project(player.x + gridSize, player.y + hw);
      const radius = pEdge ? Math.max(2, Math.abs(pEdge.x - p.x) * 0.85) : 5;
      const fixedHalfTile = fixedHalfTileRef.current ?? gridSize / 2;

      if (player.sprite) {
        const img = loadSprite(imageCacheRef.current, player.sprite);
        const spriteW = fixedHalfTile * 2;
        const spriteH = spriteW * 2;
        const sx = p.x - fixedHalfTile;
        const sy = p.y + fixedHalfTile - spriteH;
        if (img instanceof Image) {
          drawImageCoverBottom(ctx, img, sx, sy, spriteW, spriteH);
        } else {
          ctx.beginPath();
          ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
          ctx.fillStyle = "#44cc88";
          ctx.fill();
        }
      } else {
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = "#44cc88";
        ctx.fill();
        ctx.strokeStyle = "rgba(255,255,255,0.7)";
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      // Username label above
      if (player.username) {
        const fontSize = Math.max(7, radius * 0.9);
        ctx.font = `bold ${fontSize}px Silkscreen`;
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillStyle = "rgba(0,0,0,0.6)";
        ctx.fillText(player.username, p.x + 1, p.y - radius);
        ctx.fillStyle = "#ccffdd";
        ctx.fillText(player.username, p.x, p.y - radius - 1);
      }
    };

    const drawables = [];
    Object.values(spritesRef.current).forEach(({ position: pos }) => {
      if (!pos) return;
      if (pos.hidden) return;
      const entityType = pos.config?.entityType;
      const isPlayer = pos.id === "player";
      if (!entityType && !isPlayer) return;

      const hh = (pos.height || gridSize) / 2;
      drawables.push({ sortY: pos.y + hh, draw: () => drawLocalSprite(pos) });
    });
    Object.entries(remotePlayersRef.current).forEach(([id, player]) => {
      if (player.x == null || player.y == null) return;
      if ((player.level ?? 0) !== (currentLevelRef.current ?? 0)) return;
      drawables.push({ sortY: player.y + gridSize / 2, draw: () => drawRemotePlayer(player) });
    });
    drawables.sort((a, b) => a.sortY - b.sortY);
    drawables.forEach(({ draw }) => draw());

    // ── Overlay tile layer (above all entities, never solid) ──────────────────
    if (overlayTiles) {
      overlayTiles.forEach((row, r) => {
        row.forEach((tile, c) => {
          if (!tile?.bgColor && !tile?.texture && !tile?.tileset) return;

          const tl = project(c * gridSize, r * gridSize);
          const br = project((c + 1) * gridSize, (r + 1) * gridSize);
          const w = br.x - tl.x;
          const h = br.y - tl.y;
          if (w < 0.5 || h < 0.5) return;

          ctx.globalAlpha = tile.opacity ?? 1;
          drawTileCellRect(ctx, tile, tl.x, tl.y, w, h, imageCacheRef.current);
          ctx.globalAlpha = 1;
        });
      });
    }
    // ── Fog of war (pixelated — tile-sized rects) ────────────────────────────
    if (visibility === "dark" || visibility === "gloomy") {
      const playerSprite = spritesRef.current["player"];
      const pos = playerSprite?.position;
      if (pos) {
        const hw = (pos.width || gridSize) / 2;
        const hh = (pos.height || gridSize) / 2;
        const playerTileX = (pos.x + hw) / gridSize;
        const playerTileY = (pos.y + hh) / gridSize;
        const baseRadius = visibility === "dark" ? 3 : 5;
        const heldBonus = heldItemRef.current?.illumination || 0;
        const visRadius = baseRadius + heldBonus;
        const numRows = tiles.length;
        const numCols = tiles[0]?.length ?? 0;

        // Dropped items with illumination act as additional light sources
        const lightSources = droppedItems
          .filter((item) => item.illumination > 0)
          .map((item) => ({
            tileX: (item.x + gridSize / 2) / gridSize,
            tileY: (item.y + gridSize / 2) / gridSize,
            radius: item.illumination,
          }));

        ctx.fillStyle = "rgba(0,0,0,0.96)";

        for (let r = 0; r < numRows; r++) {
          for (let c = 0; c < numCols; c++) {
            const cx = c + 0.5;
            const cy = r + 0.5;
            const playerDist = Math.sqrt((cx - playerTileX) ** 2 + (cy - playerTileY) ** 2);
            if (playerDist <= visRadius) continue;
            if (lightSources.some((ls) => Math.sqrt((cx - ls.tileX) ** 2 + (cy - ls.tileY) ** 2) <= ls.radius)) continue;
            const tl = project(c * gridSize, r * gridSize);
            const br = project((c + 1) * gridSize, (r + 1) * gridSize);
            const w = br.x - tl.x;
            const h = br.y - tl.y;
            if (w < 0.5 || h < 0.5) continue;
            ctx.fillRect(tl.x, tl.y, w, h);
          }
        }

        // Fill canvas areas outside the tile grid
        const gridTL = project(0, 0);
        const gridBR = project(numCols * gridSize, numRows * gridSize);
        ctx.fillRect(0, 0, W, gridTL.y);
        ctx.fillRect(0, gridBR.y, W, H - gridBR.y);
        ctx.fillRect(0, gridTL.y, gridTL.x, gridBR.y - gridTL.y);
        ctx.fillRect(gridBR.x, gridTL.y, W - gridBR.x, gridBR.y - gridTL.y);
      }
    }
  }, [frame, tiles, decorationTiles, overlayTiles, visibility, anchor, mapInstance, theme, droppedItems]);

  return null;
};
